import Stripe from "stripe";
import { headers } from "next/headers";
import { prisma } from "@/lib/prisma";
import { stripe } from "@/lib/stripe";
import {
  sendAdminOrderNotificationEmail,
  sendOrderConfirmationEmail,
} from "@/lib/email";

function generateOrderNumber() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const rand = Math.floor(1000 + Math.random() * 9000);

  return `JJC-${y}${m}${d}-${rand}`;
}

export async function POST(req: Request) {
  const body = await req.text();
  const signature = (await headers()).get("stripe-signature");

  if (!signature) {
    return new Response("Signature Stripe manquante", { status: 400 });
  }

  let event: Stripe.Event;

  try {
    event = stripe.webhooks.constructEvent(
      body,
      signature,
      process.env.STRIPE_WEBHOOK_SECRET as string
    );
  } catch (error) {
    const message =
      error instanceof Error ? error.message : "Signature invalide";

    return new Response(`Webhook Error: ${message}`, { status: 400 });
  }

  if (event.type === "checkout.session.completed") {
    const session = event.data.object as Stripe.Checkout.Session;
    const shippingDetails = (session as Stripe.Checkout.Session & {
      shipping_details?: {
        name?: string | null;
        phone?: string | null;
        address?: {
          line1?: string | null;
          line2?: string | null;
          postal_code?: string | null;
          city?: string | null;
          country?: string | null;
        } | null;
      } | null;
    }).shipping_details;

    const userId = session.metadata?.userId;
    const cartId = session.metadata?.cartId;

    if (!userId || !cartId) {
      return new Response("Metadata manquante", { status: 400 });
    }

    const existingOrder = await prisma.order.findFirst({
      where: {
        stripeSessionId: session.id,
      },
    });

    if (existingOrder) {
      return new Response("Commande déjà traitée", { status: 200 });
    }

    const cart = await prisma.cart.findUnique({
      where: { id: cartId },
      include: {
        items: {
          include: {
            product: true,
          },
        },
        user: true,
      },
    });

    if (!cart || cart.userId !== userId || cart.items.length === 0) {
      return new Response("Panier introuvable", { status: 404 });
    }

    const subtotal = cart.items.reduce((total, item) => {
      return total + Number(item.product.price) * item.quantity;
    }, 0);

    const total = subtotal;

    const customerPhone =
      shippingDetails?.phone ||
      session.customer_details?.phone ||
      cart.user.phone ||
      null;

    const shippingAddressText = shippingDetails?.address
      ? [
          shippingDetails.name,
          shippingDetails.address.line1,
          shippingDetails.address.line2,
          `${shippingDetails.address.postal_code || ""} ${shippingDetails.address.city || ""}`.trim(),
          shippingDetails.address.country,
          customerPhone || "",
        ]
          .filter(Boolean)
          .join("\n")
      : null;

    const createdOrder = await prisma.order.create({
      data: {
        orderNumber: generateOrderNumber(),
        userId: cart.userId,
        email: cart.user.email,
        status: "PAID",
        paymentStatus: "PAID",
        subtotal: subtotal.toFixed(2),
        shippingAmount: "0.00",
        total: total.toFixed(2),
        customerFirstName: cart.user.firstName || "Cliente",
        customerLastName: cart.user.lastName || "Jour J Création",
        customerPhone,
        shippingAddressText,
        stripeSessionId: session.id,
        stripePaymentIntentId:
          typeof session.payment_intent === "string"
            ? session.payment_intent
            : null,
        items: {
          create: cart.items.map((item) => ({
            productId: item.productId,
            productNameSnapshot: item.product.name,
            productSlugSnapshot: item.product.slug,
            unitPrice: item.product.price,
            quantity: item.quantity,
            totalPrice: (
              Number(item.product.price) * item.quantity
            ).toFixed(2),
          })),
        },
      },
      include: {
        items: true,
      },
    });

    try {
      await sendOrderConfirmationEmail({
        to: cart.user.email,
        orderNumber: createdOrder.orderNumber,
        customerFirstName: cart.user.firstName,
        total: String(createdOrder.total),
        items: createdOrder.items.map((item) => ({
          productNameSnapshot: item.productNameSnapshot,
          quantity: item.quantity,
        })),
      });

      console.log("✅ Email de confirmation envoyé à :", cart.user.email);
    } catch (error) {
      console.error("❌ Erreur envoi email confirmation :", error);
    }

    try {
      await sendAdminOrderNotificationEmail({
        orderNumber: createdOrder.orderNumber,
        customerFirstName: cart.user.firstName,
        customerLastName: cart.user.lastName,
        customerEmail: cart.user.email,
        customerPhone: createdOrder.customerPhone,
        shippingAddressText: createdOrder.shippingAddressText,
        total: String(createdOrder.total),
        items: createdOrder.items.map((item) => ({
          productNameSnapshot: item.productNameSnapshot,
          quantity: item.quantity,
        })),
      });

      console.log("✅ Email admin envoyé à :", process.env.EMAIL_ADMIN);
    } catch (error) {
      console.error("❌ Erreur envoi email admin :", error);
    }

    await prisma.cartItem.deleteMany({
      where: {
        cartId: cart.id,
      },
    });

    await prisma.cart.update({
      where: { id: cart.id },
      data: {
        updatedAt: new Date(),
      },
    });
  }

  return new Response("OK", { status: 200 });
}